import { Accordion, ActionIcon, Box, Flex, Group, Modal, Progress, Stack, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconMessage, IconPlayerPlayFilled, IconQuote } from "@tabler/icons-react";
import { type JSX, useEffect, useRef, useState } from "react";
import { useTimer } from "react-timer-hook";

import { useSalesContext } from "../../../../../sales-context/SalesContext";
import styles from "./RecordedLegalDisclosuresSection.module.css";

const PENDING_TIMEOUT_MS = 5000;

// TODO: Implement a mechanism to get the actual audio length
// Hardcoded to match the {Partner}_PreRecordedDisclosures Quick Connect voicemail length in Amazon Connect. Update both together.
const DISCLOSURE_LENGTH_SECONDS = 47;

// Tolerance for "natural completion" when the SDK signals playback ended. The "ended" event
// fires roughly at the end of the recording but can race with our local timer's onExpire and
// arrive a fraction of a second early. Treat any "ended" within this window of full length as
// a successful play.
const NATURAL_END_TOLERANCE_MS = 2000;

const SCRIPT_TEXT = "I'm going to play you this quick recording of the disclosures, then we'll get you signed up.";

const DISCLOSURE_MODAL_TEXT =
    "AT&T HomeTech Protection is optional, costs $25/month plus tax, and renews monthly until canceled. " +
    "Cancel before your next billing cycle to avoid future charges, either online, by phone, or as described " +
    "in the plan Terms. Tech support begins Day 1. Breakdown and accidental damage from handling coverage on " +
    "eligible home tech begins Day 31 with repair, replacement, or reimbursement provided for approved claims. " +
    "This plan is not insurance; phones and pre-existing conditions are excluded. Claims include up to a $99 " +
    "service fee, with $2,000 per-claim and $5,000 rolling 12-month limits. Repairs and replacements may use " +
    "new, refurbished, or non-original parts. Terms, limits, exclusions, arbitration, class action waiver, and " +
    "electronic communications agreement apply at asur.me/htpterms. Plan-related communications will be sent " +
    "electronically.";

type Props = {
    disclosureEndTime: Date | null;
    onSetDisclosureEndTime: (d: Date | null) => void;
    onDisclosureCompleted: () => void;
};

export function RecordedLegalDisclosuresSection({
    disclosureEndTime,
    onSetDisclosureEndTime,
    onDisclosureCompleted,
}: Props): JSX.Element | null {
    const { features, taskData, taskType, onPlayDisclosure, disclosurePlayback, analytics, logger, partner } =
        useSalesContext();
    const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
    const [isPending, setIsPending] = useState(false);
    const pendingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Remember when playback started so we can tell natural-end (~47s elapsed) apart from
    // early-hangup on the SDK's `disclosure_playback_ended` signal — both look identical
    // (disclosurePlayback transitions to null) but only the former should mark completion.
    const startedAtRef = useRef<number | null>(null);

    const expiryTimestamp = disclosureEndTime ?? new Date(Date.now() + DISCLOSURE_LENGTH_SECONDS * 1000);

    const { totalSeconds, isRunning, restart, pause } = useTimer({
        expiryTimestamp,
        autoStart: !!disclosureEndTime,
        onExpire: () => {
            onSetDisclosureEndTime(null);
            onDisclosureCompleted();
        },
    });

    // SDK confirms the disclosure connection has actually joined the conference — anchor the
    // timer to that moment instead of the click time, so latency between click and audio-start
    // doesn't cause the progress bar to finish early.
    useEffect(() => {
        if (disclosurePlayback?.state === "playing") {
            if (pendingTimeoutRef.current) {
                clearTimeout(pendingTimeoutRef.current);
                pendingTimeoutRef.current = null;
            }
            setIsPending(false);
            startedAtRef.current = disclosurePlayback.startedAt;
            const newExpiry = new Date(disclosurePlayback.startedAt + DISCLOSURE_LENGTH_SECONDS * 1000);
            onSetDisclosureEndTime(newExpiry);
            restart(newExpiry, true);
        } else if (disclosurePlayback === null) {
            // If enough time elapsed since we started, the SDK's "ended" signal means the
            // recording finished naturally (the most common case — the Quick Connect call
            // drops on its own once the audio finishes). Mark completion before pausing,
            // because pause() prevents the local timer's onExpire from ever firing.
            const startedAt = startedAtRef.current;
            if (
                startedAt !== null &&
                Date.now() - startedAt >= DISCLOSURE_LENGTH_SECONDS * 1000 - NATURAL_END_TOLERANCE_MS
            ) {
                onDisclosureCompleted();
            }
            startedAtRef.current = null;
            // Pause so isRunning flips to false immediately. Without this, when the SDK signals
            // playback ended (e.g., customer hung up early), the timer would keep counting down
            // its original 47s and keep the button disabled.
            pause();
            onSetDisclosureEndTime(null);
        }
    }, [disclosurePlayback, onSetDisclosureEndTime, onDisclosureCompleted, restart, pause]);

    useEffect(() => {
        return () => {
            if (pendingTimeoutRef.current) clearTimeout(pendingTimeoutRef.current);
        };
    }, []);

    // isEnglishOrUnset allows undefined locale for outbound calls
    const isEnglishOrUnset = taskData.locale === "en-us" || taskData.locale === undefined;
    if (!features.isAudioDisclosuresEnabled || !taskType.isVoice || !isEnglishOrUnset || !onPlayDisclosure) {
        return null;
    }

    const handlePlay = async () => {
        analytics.dispatcher.dispatchBusinessEvent("PlayLegalDisclosure_Clicked", {
            selectedLanguage: "English",
            partner,
        });
        setIsPending(true);
        // Safety: if the SDK never confirms playback (Quick Connect misconfigured, Connect drops
        // the request silently), un-stick the button after a timeout.
        pendingTimeoutRef.current = setTimeout(() => {
            setIsPending((current) => {
                if (current) logger.warn("Disclosure playback did not start within timeout; resetting pending state");
                return false;
            });
            pendingTimeoutRef.current = null;
        }, PENDING_TIMEOUT_MS);

        try {
            await onPlayDisclosure("PreRecordedDisclosures");
        } catch (error) {
            if (pendingTimeoutRef.current) {
                clearTimeout(pendingTimeoutRef.current);
                pendingTimeoutRef.current = null;
            }
            setIsPending(false);
            logger.error({ error }, "Failed to play legal disclosure");
        }
    };

    const elapsedSeconds = isRunning || disclosureEndTime ? DISCLOSURE_LENGTH_SECONDS - totalSeconds : 0;
    const progressValue = (elapsedSeconds / DISCLOSURE_LENGTH_SECONDS) * 100;

    return (
        <>
            <Accordion.Item value="recorded-legal-disclosures">
                <Accordion.Control styles={{ control: { opacity: 1 } }}>
                    <Group justify="space-between" pr={"sm"}>
                        <Text size="xs" c="dark.1" fw="var(--mantine-font-weight-heavy)">
                            Play Disclosures (required)
                        </Text>
                    </Group>
                </Accordion.Control>
                <Accordion.Panel>
                    <Stack gap="md" p="sm">
                        <Text fw={700} size="md">
                            <Text span c="teal.4" inherit>
                                Play
                            </Text>{" "}
                            the disclosures
                        </Text>

                        <Group align="flex-start" gap="xs" wrap="nowrap">
                            <IconQuote
                                size={16}
                                color="var(--mantine-color-teal-4)"
                                className={styles.quoteIconFlipped}
                            />
                            <Text size="sm" c="dark.0">
                                {SCRIPT_TEXT}
                            </Text>
                        </Group>

                        <Stack gap={4}>
                            <Group justify="space-between">
                                <Text size="xs" c="dark.1" ff="monospace">
                                    {formatTime(elapsedSeconds)}
                                </Text>
                                <Text size="xs" c="dark.1" ff="monospace">
                                    {formatTime(DISCLOSURE_LENGTH_SECONDS)}
                                </Text>
                            </Group>
                            <Progress
                                value={progressValue}
                                color="teal.4"
                                size="sm"
                                classNames={{ section: styles.progressBarSection }}
                            />
                        </Stack>

                        <Box pos="relative">
                            <Flex justify="center">
                                <ActionIcon
                                    size={48}
                                    radius="xl"
                                    color="violet.6"
                                    onClick={handlePlay}
                                    disabled={isPending || isRunning}
                                    aria-label="Play disclosure"
                                >
                                    <IconPlayerPlayFilled size={20} />
                                </ActionIcon>
                            </Flex>
                            <ActionIcon
                                pos="absolute"
                                right={0}
                                top="50%"
                                style={{ transform: "translateY(-50%)" }}
                                variant="subtle"
                                color="gray"
                                onClick={openModal}
                                aria-label="View disclosure text"
                            >
                                <IconMessage size={20} />
                            </ActionIcon>
                        </Box>
                    </Stack>
                </Accordion.Panel>
            </Accordion.Item>

            <Modal opened={modalOpened} onClose={closeModal} title="Disclosure text" size="lg" centered>
                <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
                    {DISCLOSURE_MODAL_TEXT}
                </Text>
            </Modal>
        </>
    );
}

function formatTime(seconds: number): string {
    const safe = Math.max(0, Math.floor(seconds));
    const mins = Math.floor(safe / 60);
    const secs = safe % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
}
